import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const ENQUIRY_TYPES = [
  { v: "investor", l: "Investor enquiry" },
  { v: "partnership", l: "Partnership / building owner" },
  { v: "press", l: "Press" },
  { v: "general", l: "General" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", enquiry_type: "investor", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thank you. We'll be in touch.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="hm-container pt-40 pb-24 md:pb-28">
        <p className="hm-eyebrow mb-8">Contact / Investor Enquiry</p>
        <h1 className="font-display leading-[0.98] tracking-[-0.02em] max-w-4xl" style={{ fontSize: "clamp(44px, 6.4vw, 92px)", fontWeight: 400 }}>
          Tell us what you&rsquo;re working on.
        </h1>
        <p className="mt-10 text-[color:var(--hm-ink-dim)] text-[17px] max-w-2xl leading-relaxed">
          We respond to genuine enquiries &mdash; investment, partnerships, buildings, press.
        </p>
      </section>

      <section className="hm-container pb-36">
        <div className="grid md:grid-cols-12 gap-14">
          <div className="md:col-span-5 space-y-10">
            <div>
              <p className="hm-eyebrow mb-4">Direct</p>
              <a href="mailto:hi@har-monic.com" className="hm-link text-[18px]" data-testid="contact-email">hi@har-monic.com</a>
            </div>
            <div>
              <p className="hm-eyebrow mb-4">Office</p>
              <p className="text-[color:var(--hm-ink-dim)] leading-relaxed text-[15px]">
                Har-monic Development Ltd.<br />
                London, United Kingdom
              </p>
            </div>
            <div>
              <p className="hm-eyebrow mb-4">Press</p>
              <p className="text-[color:var(--hm-ink-dim)] leading-relaxed text-[15px]">
                For press enquiries, select <em>Press</em> in the form. We&rsquo;ll route accordingly.
              </p>
            </div>
          </div>

          <div className="md:col-span-7">
            {sent ? (
              <div className="border border-[color:var(--hm-pink)] p-12" data-testid="contact-success">
                <p className="hm-eyebrow mb-5" style={{ color: "var(--hm-pink)" }}>Received</p>
                <h2 className="font-display leading-tight mb-4" style={{ fontSize: "40px", fontWeight: 400 }}>Thank you.</h2>
                <p className="text-[color:var(--hm-ink-dim)] text-[16px] leading-relaxed">
                  Your enquiry is with us. We read everything and respond directly — usually within a few working days.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <input name="name" value={form.name} onChange={onChange} placeholder="Your name" className="hm-input" required data-testid="contact-name" />
                  <input name="email" type="email" value={form.email} onChange={onChange} placeholder="Email address" className="hm-input" required data-testid="contact-email-input" />
                </div>
                <input name="organisation" value={form.organisation} onChange={onChange} placeholder="Organisation (optional)" className="hm-input" data-testid="contact-org" />
                <div>
                  <p className="hm-eyebrow mb-4">Enquiry type</p>
                  <div className="flex flex-wrap gap-2">
                    {ENQUIRY_TYPES.map((t) => (
                      <button
                        type="button"
                        key={t.v}
                        onClick={() => setForm({ ...form, enquiry_type: t.v })}
                        data-testid={`contact-type-${t.v}`}
                        className={`px-4 py-2.5 text-[13px] border rounded-full transition-colors ${form.enquiry_type === t.v ? "bg-[color:var(--hm-pink)] text-[#231f20] border-[color:var(--hm-pink)]" : "border-[color:var(--hm-line)] text-[color:var(--hm-ink-dim)] hover:border-[color:var(--hm-pink)] hover:text-[color:var(--hm-pink)]"}`}
                      >
                        {t.l}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={7}
                  placeholder="Your message — please include relevant detail."
                  className="hm-input"
                  required
                  data-testid="contact-message"
                />
                <button type="submit" disabled={sending} className="hm-btn" data-testid="contact-submit">
                  {sending ? "Sending…" : "Send enquiry →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
